import {
  CampaignLevelOfWarfare,
  type CampaignSelectDto,
  type MissionListSelectDto,
  type StudyListSelectDto,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ClassificationBadge } from '@components/shared/classification-badge.component';
import { ConfirmDeleteModal, ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { EntityPickerPopover } from '@components/shared/entity-picker-popover.component';
import { FormLabel } from '@components/shared/form-label.component';
import { ModalSteps, StepConfig, useSteps } from '@components/shared/modal-steps.component';
import { StudyStatusBadge } from '@components/shared/study-status-badge.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useAppForm, withForm } from '@components/ui/form/form';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { errorToast, successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useCreateCampaignWithChildren, useDeleteCampaign, useUpdateCampaign } from '@queries/campaigns.query';
import { useGetIntelMissions, useGetMeiaStudies } from '@queries/meia.query';
import {
  campaignDecisivePointDescriptionSchema,
  campaignNameSchema,
  campaignScenarioNameSchema,
  campaignVignetteNameSchema,
} from '@schemas/campaign.schema';
import { type DeepKeys, useStore } from '@tanstack/react-form';
import { Trash2Icon, TrashIcon } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { campaignFormOptions } from './campaign-modal-form-options';
import {
  CAMPAIGN_MODAL_STEP_LABELS,
  CLASSIFICATION_OPTIONS,
  COALITION_PARTNER_OPTIONS,
  type CampaignDraft,
  CampaignModalStepKey,
  DECISIVE_POINT_RANK_OPTIONS,
  type DecisivePointDraft,
  KILL_CHAIN_CATEGORY_OPTIONS,
  LEAD_COMMAND_OPTIONS,
  LEVEL_OF_WARFARE_OPTIONS,
  OPERATIONAL_REGION_LABELS,
  OPERATIONAL_REGION_OPTIONS,
  STATUS_OPTIONS,
  type ScenarioDraft,
  type VignetteDraft,
  type VignetteMissionRefDraft,
  campaignDraftToUpdateDto,
  campaignToDraft,
  decisivePointDraftToCreateDto,
  emptyCampaignDraft,
  emptyDecisivePointDraft,
  emptyScenarioDraft,
  emptyVignetteDraft,
  scenarioDraftToCreateDto,
} from './campaign-modal.shared';

type CampaignDraftFieldPath = DeepKeys<CampaignDraft>;
type CampaignDraftFieldMeta = Partial<Record<CampaignDraftFieldPath, { errors?: readonly unknown[] }>>;
type CampaignDraftFieldPrefix =
  | 'decisivePoints'
  | 'name'
  | 'scenarios'
  | `decisivePoints[${number}]`
  | `scenarios[${number}]`
  | `scenarios[${number}].vignettes[${number}]`;
type DecisivePointDraftField = Extract<keyof DecisivePointDraft, string>;
type ScenarioDraftField = Extract<keyof ScenarioDraft, string>;
type VignetteDraftField = Extract<keyof VignetteDraft, string>;

const formatIndexedLabel = (prefix: string, index: number): string => `${prefix}-${String(index + 1).padStart(3, '0')}`;

const hasPrefixedFieldError = (
  fieldMeta: CampaignDraftFieldMeta | undefined,
  prefix: CampaignDraftFieldPrefix,
): boolean => {
  if (!fieldMeta) return false;
  return Object.entries(fieldMeta).some(
    ([path, meta]) => path.startsWith(prefix) && meta?.errors && meta.errors.length > 0,
  );
};

const STEP_FIELD_PREFIX: Partial<Record<CampaignModalStepKey, string>> = {
  [CampaignModalStepKey.DETAILS]: 'name',
  [CampaignModalStepKey.DECISIVE_POINTS]: 'decisivePoints',
  [CampaignModalStepKey.SCENARIOS]: 'scenarios',
};

const decisivePointPrefix = (decisivePointIndex: number): `decisivePoints[${number}]` =>
  `decisivePoints[${decisivePointIndex}]`;

const decisivePointPath = <TField extends DecisivePointDraftField>(
  decisivePointIndex: number,
  field: TField,
): `decisivePoints[${number}].${TField}` => `decisivePoints[${decisivePointIndex}].${field}`;

const scenarioPrefix = (scenarioIndex: number): `scenarios[${number}]` => `scenarios[${scenarioIndex}]`;

const scenarioPath = <TField extends ScenarioDraftField>(
  scenarioIndex: number,
  field: TField,
): `scenarios[${number}].${TField}` => `scenarios[${scenarioIndex}].${field}`;

const vignettePrefix = (scenarioIndex: number, vignetteIndex: number): `scenarios[${number}].vignettes[${number}]` =>
  `scenarios[${scenarioIndex}].vignettes[${vignetteIndex}]`;

const vignettePath = <TField extends VignetteDraftField>(
  scenarioIndex: number,
  vignetteIndex: number,
  field: TField,
): `scenarios[${number}].vignettes[${number}].${TField}` =>
  `scenarios[${scenarioIndex}].vignettes[${vignetteIndex}].${field}`;

const STEP_DATA: StepConfig[] = [
  { label: CAMPAIGN_MODAL_STEP_LABELS[CampaignModalStepKey.DETAILS], step: CampaignModalStepKey.DETAILS },
  {
    label: CAMPAIGN_MODAL_STEP_LABELS[CampaignModalStepKey.DECISIVE_POINTS],
    step: CampaignModalStepKey.DECISIVE_POINTS,
  },
  { label: CAMPAIGN_MODAL_STEP_LABELS[CampaignModalStepKey.SCENARIOS], step: CampaignModalStepKey.SCENARIOS },
  {
    label: CAMPAIGN_MODAL_STEP_LABELS[CampaignModalStepKey.CONSUMED_STUDIES],
    step: CampaignModalStepKey.CONSUMED_STUDIES,
  },
];

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiErrorWrapper) {
    return error.errors[0]?.message ?? fallback;
  }
  return fallback;
};

type CampaignModalProps =
  | {
      isOpen: boolean;
      mode: 'create';
      onClose: () => void;
      onCreateSuccess: (campaignId: string) => void;
    }
  | {
      campaign: CampaignSelectDto;
      initialStep?: CampaignModalStepKey;
      isOpen: boolean;
      mode: 'edit';
      onClose: () => void;
      onDeleted: () => void;
    };

export const CampaignModal: FC<CampaignModalProps> = (props) => {
  const isEdit = props.mode === 'edit';
  const initialStep = isEdit ? (props.initialStep ?? CampaignModalStepKey.DETAILS) : CampaignModalStepKey.DETAILS;
  const editCampaign = props.mode === 'edit' ? props.campaign : null;
  const { isOpen, mode, onClose } = props;

  // Snapshot the campaign on open so mid-save refetches don't reset the form.
  const snapshotRef = useRef<CampaignDraft | null>(null);
  if (snapshotRef.current === null && editCampaign && isOpen) {
    snapshotRef.current = campaignToDraft(editCampaign);
  }

  const initialDefaults = useMemo<CampaignDraft>(
    () => snapshotRef.current ?? (editCampaign ? campaignToDraft(editCampaign) : emptyCampaignDraft()),
    [editCampaign],
  );

  const createCampaignWithChildren = useCreateCampaignWithChildren();
  const updateCampaign = useUpdateCampaign();
  const deleteCampaign = useDeleteCampaign();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isUnsavedConfirmOpen, setIsUnsavedConfirmOpen] = useState(false);

  const form = useAppForm({
    ...campaignFormOptions,
    defaultValues: initialDefaults,
    onSubmit: async ({ value }) => {
      if (mode === 'create') {
        try {
          const created = await createCampaignWithChildren.mutateAsync({
            classificationLevel: value.classificationLevel,
            coalitionPartners: value.coalitionPartners,
            consumedStudyIds: value.consumedStudyIds,
            decisivePoints: value.decisivePoints.map(decisivePointDraftToCreateDto),
            description: value.description.trim() || null,
            killChainCategory: value.killChainCategory,
            leadCommands: value.leadCommands,
            name: value.name.trim(),
            scenarios: value.scenarios.map(scenarioDraftToCreateDto),
            status: value.status,
            timeWindow: value.timeWindow.trim() || null,
          });
          successToast({ description: 'Campaign created.' });
          handleCloseInternal();
          props.onCreateSuccess(created.id);
        } catch (error) {
          errorToast({ description: extractApiErrorMessage(error, 'Failed to create campaign.') });
        }
      } else if (editCampaign) {
        try {
          await updateCampaign.mutateAsync({
            campaignId: editCampaign.id,
            dto: campaignDraftToUpdateDto(value),
          });
          successToast({ description: 'Campaign saved.' });
          handleCloseInternal();
        } catch (error) {
          errorToast({ description: extractApiErrorMessage(error, 'Failed to save campaign.') });
        }
      }
    },
  });

  const { handleStepClick, resetSteps, step, stepsInfo, updateStepState } = useSteps(STEP_DATA, initialStep, isEdit);

  useEffect(() => {
    if (!isOpen) {
      // Reset snapshot when the modal closes so a re-open captures the latest server state.
      snapshotRef.current = null;
      return;
    }
    form.reset(initialDefaults);
  }, [isOpen, initialDefaults, form]);

  const handleCloseInternal = useCallback(() => {
    resetSteps();
    snapshotRef.current = null;
    if (mode === 'create') {
      form.reset(emptyCampaignDraft());
    }
    onClose();
  }, [mode, onClose, resetSteps, form]);

  const isSaving = createCampaignWithChildren.isPending || updateCampaign.isPending;

  const handleCloseRequest = useCallback(() => {
    if (isSaving) return;
    if (form.state.isDirty) {
      setIsUnsavedConfirmOpen(true);
      return;
    }
    handleCloseInternal();
  }, [form.state.isDirty, handleCloseInternal, isSaving]);

  const handleConfirmDiscard = useCallback(() => {
    setIsUnsavedConfirmOpen(false);
    handleCloseInternal();
  }, [handleCloseInternal]);

  const validateCurrentStep = async (): Promise<boolean> => {
    await form.validateAllFields('submit');
    const prefix = STEP_FIELD_PREFIX[step];
    if (!prefix) return true;
    const hasStepErrors = hasPrefixedFieldError(form.state.fieldMeta, prefix);
    if (hasStepErrors) {
      errorToast({ description: 'Fix the highlighted fields before continuing.' });
      return false;
    }
    return true;
  };

  const handleNextCreate = async (): Promise<void> => {
    if (!(await validateCurrentStep())) return;
    if (step === CampaignModalStepKey.CONSUMED_STUDIES) {
      await form.handleSubmit();
      return;
    }
    updateStepState(step);
  };

  const handleBackCreate = (): void => {
    if (step > CampaignModalStepKey.DETAILS) {
      handleStepClick(step - 1);
    }
  };

  const handleStepClickWithValidation = async (targetStep: number): Promise<void> => {
    if (targetStep === step) return;
    if (targetStep > step && !(await validateCurrentStep())) return;
    handleStepClick(targetStep);
  };

  const handleEditSave = async (): Promise<void> => {
    if (!editCampaign) return;
    await form.handleSubmit();
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!editCampaign || props.mode !== 'edit') return;
    try {
      await deleteCampaign.mutateAsync({
        campaignId: editCampaign.id,
        consumedStudyIds: editCampaign.consumedStudies.map((link) => link.studyId),
      });
      setIsDeleteOpen(false);
      handleCloseInternal();
      props.onDeleted();
    } catch (error) {
      errorToast({ description: extractApiErrorMessage(error, 'Failed to delete campaign.') });
    }
  };

  const rightLabel = isEdit
    ? updateCampaign.isPending
      ? 'Saving…'
      : 'Save'
    : step === CampaignModalStepKey.CONSUMED_STUDIES
      ? createCampaignWithChildren.isPending
        ? 'Creating…'
        : 'Create Campaign'
      : 'Continue';

  return (
    <Modal
      footer={
        isEdit
          ? {
              leftButton: (
                <Button
                  className="border-red-500 bg-red-100 text-red-600 hover:bg-red-200"
                  disabled={isSaving || deleteCampaign.isPending}
                  onClick={() => setIsDeleteOpen(true)}
                  type="button"
                  variant="secondary"
                >
                  <TrashIcon className="size-4" />
                  Delete
                </Button>
              ),
              rightButton: {
                children: rightLabel,
                disabled: isSaving || deleteCampaign.isPending,
                onClick: () => void handleEditSave(),
                type: 'button',
              },
            }
          : {
              leftButton:
                step !== CampaignModalStepKey.DETAILS
                  ? { children: 'Back', onClick: handleBackCreate, variant: 'secondary' }
                  : undefined,
              rightButton: {
                children: rightLabel,
                disabled: createCampaignWithChildren.isPending,
                onClick: () => void handleNextCreate(),
              },
            }
      }
      header={{ title: isEdit ? 'Edit Campaign' : 'Create Campaign' }}
      isOpen={isOpen}
      onClose={handleCloseRequest}
      width="760px"
    >
      <form.AppForm>
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <ModalSteps
            onStepClick={(target) => void handleStepClickWithValidation(target)}
            stepsInfo={stepsInfo}
          />

          {step === CampaignModalStepKey.DETAILS && <CampaignDetailsStep form={form} />}

          {step === CampaignModalStepKey.DECISIVE_POINTS && <CampaignDecisivePointsStep form={form} />}

          {step === CampaignModalStepKey.SCENARIOS && <CampaignScenariosStep form={form} />}

          {step === CampaignModalStepKey.CONSUMED_STUDIES && <CampaignConsumedStudiesStep form={form} />}
        </form>
      </form.AppForm>
      {isEdit && editCampaign && (
        <ConfirmDeleteModal
          isOpen={isDeleteOpen}
          itemName={`campaign "${editCampaign.name}"`}
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsDeleteOpen}
        />
      )}
      <ConfirmationModal
        cancelLabel="Keep Editing"
        confirmLabel="Discard Changes"
        description={<p>You have unsaved changes. Discard them and close this campaign?</p>}
        isOpen={isUnsavedConfirmOpen}
        onConfirm={handleConfirmDiscard}
        setIsOpen={setIsUnsavedConfirmOpen}
        title="Discard unsaved changes?"
        variant="destructive"
      />
    </Modal>
  );
};

const CampaignDetailsStep = withForm({
  ...campaignFormOptions,
  props: {},
  render: function Render({ form }) {
    return (
      <div className="flex flex-col gap-4">
        <form.AppField
          name="name"
          validators={{ onSubmit: campaignNameSchema }}
        >
          {(field) => (
            <field.TextField
              label="Campaign Name"
              placeholder="e.g., Pacific Sentinel 2027"
              required
            />
          )}
        </form.AppField>

        <form.AppField name="description">
          {(field) => (
            <field.TextAreaField
              label="Description"
              placeholder="Short campaign-level synopsis."
              rows={3}
            />
          )}
        </form.AppField>

        <div className="grid grid-cols-2 gap-4">
          <form.AppField name="status">
            {(field) => (
              <field.SelectField
                label="Status"
                options={STATUS_OPTIONS}
              />
            )}
          </form.AppField>
          <form.AppField name="classificationLevel">
            {(field) => (
              <field.SelectField
                label="Classification"
                options={CLASSIFICATION_OPTIONS}
              />
            )}
          </form.AppField>
        </div>

        <form.AppField name="leadCommands">
          {(field) => (
            <field.SelectMultipleField
              label="Lead Commands"
              options={LEAD_COMMAND_OPTIONS}
              placeholder="Select lead commands"
            />
          )}
        </form.AppField>

        <form.AppField name="coalitionPartners">
          {(field) => (
            <field.SelectMultipleField
              label="Coalition Partners"
              options={COALITION_PARTNER_OPTIONS}
              placeholder="Select coalition partners"
            />
          )}
        </form.AppField>

        <div className="grid grid-cols-2 gap-4">
          <form.AppField name="timeWindow">
            {(field) => (
              <field.TextField
                label="Time Window"
                placeholder="FY26-27, T+0 through T+21d, etc."
              />
            )}
          </form.AppField>
          <form.AppField name="killChainCategory">
            {(field) => (
              <field.SelectField
                clearable
                label="Mission Type Tag"
                options={KILL_CHAIN_CATEGORY_OPTIONS}
                placeholder="Select Mission Type Tag"
              />
            )}
          </form.AppField>
        </div>
      </div>
    );
  },
});

const CampaignDecisivePointsStep = withForm({
  ...campaignFormOptions,
  props: {},
  render: function Render({ form }) {
    return (
      <form.AppField
        mode="array"
        name="decisivePoints"
      >
        {(field) => {
          const rows = field.state.value ?? [];
          return rows.length === 0 ? (
            <EmptyStateContainer
              className="min-h-[240px]"
              message="+ Add Decisive Point"
              onClick={() => field.pushValue({ ...emptyDecisivePointDraft(), position: rows.length + 1 })}
              title="No Decisive Points"
            />
          ) : (
            <div className="flex flex-col gap-4">
              {rows.map((row, index) => (
                <form.Subscribe
                  key={row.clientId}
                  selector={(state) => hasPrefixedFieldError(state.fieldMeta, decisivePointPrefix(index))}
                >
                  {(rowHasError) => (
                    <div
                      className={cn(
                        'flex flex-col gap-2 rounded-md border p-4',
                        rowHasError ? 'border-red-500' : 'border-gray-200',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-500">{`DP${index + 1}`}</span>
                        <IconButton
                          ariaLabel="Remove decisive point"
                          icon={<Trash2Icon />}
                          onClick={() => field.removeValue(index)}
                          size="md"
                          variant="secondary"
                        />
                      </div>
                      <form.AppField
                        name={decisivePointPath(index, 'description')}
                        validators={{ onSubmit: campaignDecisivePointDescriptionSchema }}
                      >
                        {(descriptionField) => (
                          <descriptionField.TextAreaField
                            label="Description"
                            placeholder="What must succeed for the campaign to advance?"
                            required
                            rows={2}
                          />
                        )}
                      </form.AppField>
                      <div className="grid grid-cols-2 gap-4">
                        <form.AppField name={decisivePointPath(index, 'position')}>
                          {(positionField) => (
                            <positionField.SelectField
                              label="Rank"
                              options={DECISIVE_POINT_RANK_OPTIONS}
                              placeholder="Select rank"
                            />
                          )}
                        </form.AppField>
                        <form.AppField name={decisivePointPath(index, 'location')}>
                          {(locationField) => (
                            <locationField.TextField
                              label="Location"
                              placeholder="e.g., First Island Chain"
                            />
                          )}
                        </form.AppField>
                      </div>
                      <form.AppField
                        mode="array"
                        name={decisivePointPath(index, 'evidence')}
                      >
                        {(evidenceField) => (
                          <evidenceField.InputChipField
                            description="Type evidence and press Enter to add them."
                            label="Evidence"
                            placeholder="Type evidence and press Enter to add..."
                          />
                        )}
                      </form.AppField>
                    </div>
                  )}
                </form.Subscribe>
              ))}
              <Button
                className="self-start"
                onClick={() => field.pushValue({ ...emptyDecisivePointDraft(), position: rows.length + 1 })}
                type="button"
                variant="link"
              >
                + Add Decisive Point
              </Button>
            </div>
          );
        }}
      </form.AppField>
    );
  },
});

const CampaignScenariosStep = withForm({
  ...campaignFormOptions,
  props: {},
  render: function Render({ form }) {
    return (
      <form.AppField
        mode="array"
        name="scenarios"
      >
        {(field) => {
          const rows = field.state.value ?? [];
          const campaignClassification = form.state.values.classificationLevel;
          return rows.length === 0 ? (
            <EmptyStateContainer
              className="min-h-[240px]"
              message="+ Add Scenario"
              onClick={() => field.pushValue(emptyScenarioDraft({ classificationLevel: campaignClassification }))}
              title="No Scenarios"
            />
          ) : (
            <div className="flex flex-col gap-4">
              {rows.map((row, scenarioIndex) => (
                <ScenarioRow
                  form={form}
                  key={row.clientId}
                  onRemove={() => field.removeValue(scenarioIndex)}
                  scenarioIndex={scenarioIndex}
                />
              ))}
              <Button
                className="self-start"
                onClick={() => field.pushValue(emptyScenarioDraft({ classificationLevel: campaignClassification }))}
                type="button"
                variant="link"
              >
                + Add Scenario
              </Button>
            </div>
          );
        }}
      </form.AppField>
    );
  },
});

const noopHandler: () => void = () => {};

const ScenarioRow = withForm({
  ...campaignFormOptions,
  props: {
    onRemove: noopHandler,
    scenarioIndex: 0,
  },
  render: function Render({ form, onRemove, scenarioIndex }) {
    const levelOfWarfare = useStore(form.store, (state) => state.values.scenarios[scenarioIndex]?.levelOfWarfare);
    const isLowOverride = levelOfWarfare !== CampaignLevelOfWarfare.OPERATIONAL;

    return (
      <form.Subscribe selector={(state) => hasPrefixedFieldError(state.fieldMeta, scenarioPrefix(scenarioIndex))}>
        {(rowHasError) => (
          <div
            className={cn(
              'flex flex-col gap-3 rounded-md border p-4',
              rowHasError ? 'border-red-500' : 'border-gray-200',
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">{formatIndexedLabel('SCN', scenarioIndex)}</span>
              <IconButton
                ariaLabel="Remove scenario"
                icon={<Trash2Icon />}
                onClick={onRemove}
                size="md"
                variant="secondary"
              />
            </div>
            <form.AppField
              name={scenarioPath(scenarioIndex, 'name')}
              validators={{ onSubmit: campaignScenarioNameSchema }}
            >
              {(nameField) => (
                <nameField.TextField
                  label="Name"
                  placeholder="e.g., Counter-A2/AD Phase II"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name={scenarioPath(scenarioIndex, 'description')}>
              {(descField) => (
                <descField.TextAreaField
                  label="Description"
                  placeholder="What does this scenario rehearse?"
                  rows={2}
                />
              )}
            </form.AppField>
            <div className="grid grid-cols-2 gap-4">
              <form.AppField name={scenarioPath(scenarioIndex, 'classificationLevel')}>
                {(classificationField) => (
                  <classificationField.SelectField
                    label="Classification"
                    options={CLASSIFICATION_OPTIONS}
                  />
                )}
              </form.AppField>
              <form.AppField name={scenarioPath(scenarioIndex, 'levelOfWarfare')}>
                {(lowField) => (
                  <lowField.SelectField
                    label="Level of Warfare"
                    options={LEVEL_OF_WARFARE_OPTIONS}
                  />
                )}
              </form.AppField>
            </div>
            {isLowOverride && (
              <form.AppField name={scenarioPath(scenarioIndex, 'levelOfWarfareOverrideRationale')}>
                {(rationaleField) => (
                  <rationaleField.TextField
                    label="Override Rationale"
                    placeholder="Explain why LoW differs from Operational"
                    required
                  />
                )}
              </form.AppField>
            )}
            <form.AppField name={scenarioPath(scenarioIndex, 'operationalRegions')}>
              {(regionsField) => (
                <regionsField.SelectMultipleField
                  label="Operational Regions"
                  options={OPERATIONAL_REGION_OPTIONS}
                  placeholder="Select regions"
                />
              )}
            </form.AppField>

            <VignettesSection
              form={form}
              scenarioIndex={scenarioIndex}
            />
          </div>
        )}
      </form.Subscribe>
    );
  },
});

const VignettesSection = withForm({
  ...campaignFormOptions,
  props: {
    scenarioIndex: 0,
  },
  render: function Render({ form, scenarioIndex }) {
    return (
      <form.AppField
        mode="array"
        name={scenarioPath(scenarioIndex, 'vignettes')}
      >
        {(field) => {
          const rows = field.state.value ?? [];
          return rows.length === 0 ? (
            <EmptyStateContainer
              className="min-h-[180px]"
              message="+ Add Vignette"
              onClick={() => field.pushValue(emptyVignetteDraft())}
              title="No Vignettes"
            />
          ) : (
            <div className="flex flex-col gap-4">
              {rows.map((row, vignetteIndex) => (
                <VignetteRow
                  form={form}
                  key={row.clientId}
                  onRemove={() => field.removeValue(vignetteIndex)}
                  scenarioIndex={scenarioIndex}
                  vignetteIndex={vignetteIndex}
                />
              ))}
              <Button
                className="self-start"
                onClick={() => field.pushValue(emptyVignetteDraft())}
                type="button"
                variant="link"
              >
                + Add Vignette
              </Button>
            </div>
          );
        }}
      </form.AppField>
    );
  },
});

const VignetteRow = withForm({
  ...campaignFormOptions,
  props: {
    onRemove: noopHandler,
    scenarioIndex: 0,
    vignetteIndex: 0,
  },
  render: function Render({ form, onRemove, scenarioIndex, vignetteIndex }) {
    const overrideRegions = useStore(
      form.store,
      (state) => state.values.scenarios[scenarioIndex]?.vignettes[vignetteIndex]?.operationalRegionsOverride ?? [],
    );
    const parentRegions = useStore(
      form.store,
      (state) => state.values.scenarios[scenarioIndex]?.operationalRegions ?? [],
    );
    const hasOverride = overrideRegions.length > 0;
    const attachedMissions = useStore(
      form.store,
      (state) => state.values.scenarios[scenarioIndex]?.vignettes[vignetteIndex]?.missions ?? [],
    );

    return (
      <form.Subscribe
        selector={(state) => hasPrefixedFieldError(state.fieldMeta, vignettePrefix(scenarioIndex, vignetteIndex))}
      >
        {(rowHasError) => (
          <div
            className={cn(
              'flex flex-col gap-3 rounded-md border p-4',
              rowHasError ? 'border-red-500' : 'border-gray-200',
            )}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">{formatIndexedLabel('VGN', vignetteIndex)}</span>
              <IconButton
                ariaLabel="Remove vignette"
                icon={<Trash2Icon />}
                onClick={onRemove}
                size="md"
                variant="secondary"
              />
            </div>
            <form.AppField
              name={vignettePath(scenarioIndex, vignetteIndex, 'name')}
              validators={{ onSubmit: campaignVignetteNameSchema }}
            >
              {(nameField) => (
                <nameField.TextField
                  label="Name"
                  placeholder="e.g., Day 3 SEAD push"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name={vignettePath(scenarioIndex, vignetteIndex, 'description')}>
              {(descField) => (
                <descField.TextAreaField
                  label="Description"
                  placeholder="Describe the vignette."
                  rows={2}
                />
              )}
            </form.AppField>

            <div className="flex flex-col gap-1.5">
              <FormLabel label="Operational Regions (override)" />
              <p className="text-xs text-gray-500">Leave empty to inherit from the scenario.</p>
              {!hasOverride && parentRegions.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Inherited</span>
                  {parentRegions.map((region) => (
                    <Badge
                      key={region}
                      size="xs"
                      variant="outline"
                    >
                      {OPERATIONAL_REGION_LABELS[region]}
                    </Badge>
                  ))}
                </div>
              )}
              <form.AppField name={vignettePath(scenarioIndex, vignetteIndex, 'operationalRegionsOverride')}>
                {(overrideField) => (
                  <overrideField.SelectMultipleField
                    options={OPERATIONAL_REGION_OPTIONS}
                    placeholder="Add regions to override"
                  />
                )}
              </form.AppField>
            </div>
            {hasOverride && (
              <form.AppField name={vignettePath(scenarioIndex, vignetteIndex, 'operationalRegionsOverrideRationale')}>
                {(rationaleField) => (
                  <rationaleField.TextField
                    label="Region Override Rationale"
                    placeholder="Why does this vignette override the scenario regions?"
                    required
                  />
                )}
              </form.AppField>
            )}

            <VignetteMissionsPicker
              attachedMissions={attachedMissions}
              onChange={(missions) =>
                form.setFieldValue(vignettePath(scenarioIndex, vignetteIndex, 'missions'), missions)
              }
            />
          </div>
        )}
      </form.Subscribe>
    );
  },
});

type VignetteMissionsPickerProps = {
  attachedMissions: VignetteMissionRefDraft[];
  onChange: (missions: VignetteMissionRefDraft[]) => void;
};

const filterMissionsBySearch = (params: {
  attachedMissions: VignetteMissionRefDraft[];
  missions: MissionListSelectDto[];
  search: string;
}): MissionListSelectDto[] => {
  const { attachedMissions, missions, search } = params;
  const attachedIds = new Set(attachedMissions.map((mission) => mission.id));
  const query = search.toLowerCase();
  return missions
    .filter((mission) => !attachedIds.has(mission.id))
    .filter(
      (mission) =>
        !query || mission.name.toLowerCase().includes(query) || (mission.studyName ?? '').toLowerCase().includes(query),
    );
};

const filterStudiesBySearch = (params: {
  attachedIds: Set<string>;
  search: string;
  studies: StudyListSelectDto[];
}): StudyListSelectDto[] => {
  const { attachedIds, search, studies } = params;
  const query = search.toLowerCase();
  return studies
    .filter((study) => !attachedIds.has(study.id))
    .filter((study) => !query || study.name.toLowerCase().includes(query));
};

const VignetteMissionsPicker: FC<VignetteMissionsPickerProps> = (props) => {
  const { attachedMissions, onChange } = props;
  const { data: allMissions = [], isLoading } = useGetIntelMissions();
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => filterMissionsBySearch({ attachedMissions, missions: allMissions, search }),
    [allMissions, search, attachedMissions],
  );

  const handleAdd = (mission: MissionListSelectDto): void => {
    onChange([
      ...attachedMissions,
      { id: mission.id, name: mission.name, studyId: mission.studyId, studyName: mission.studyName ?? null },
    ]);
  };

  const handleRemove = (missionId: string): void => {
    onChange(attachedMissions.filter((mission) => mission.id !== missionId));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <FormLabel label="Mission References" />
        <EntityPickerPopover<MissionListSelectDto>
          addButtonLabel="Add Mission"
          emptyMessage="No available missions match this search."
          isLoading={isLoading}
          onAdd={handleAdd}
          onSearchChange={setSearch}
          options={filtered}
          placeholder="Search missions"
          renderOption={(mission) => (
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{mission.name}</p>
              <p className="truncate text-xs text-muted-foreground">{mission.studyName ?? '—'}</p>
            </div>
          )}
          search={search}
        />
      </div>
      {attachedMissions.length === 0 ? (
        <p className="text-xs text-gray-500">No missions linked.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {attachedMissions.map((mission) => (
            <div
              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-1.5"
              key={mission.id}
            >
              <div className="flex flex-col">
                <span className="text-sm text-gray-800">{mission.name}</span>
                <span className="text-xs text-gray-500">{mission.studyName ?? '—'}</span>
              </div>
              <IconButton
                ariaLabel="Remove mission"
                icon={<Trash2Icon />}
                onClick={() => handleRemove(mission.id)}
                size="md"
                variant="secondary"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CampaignConsumedStudiesStep = withForm({
  ...campaignFormOptions,
  props: {},
  render: function Render({ form }) {
    const { data: studies = [], isLoading } = useGetMeiaStudies();
    const [search, setSearch] = useState('');

    const studyIds = useStore(form.store, (state) => state.values.consumedStudyIds);

    const filtered = useMemo(
      () => filterStudiesBySearch({ attachedIds: new Set(studyIds), search, studies }),
      [studies, search, studyIds],
    );

    const attachedStudies = studyIds
      .map((id) => studies.find((study) => study.id === id))
      .filter((study): study is StudyListSelectDto => Boolean(study));

    const setIds = (nextIds: string[]): void => {
      form.setFieldValue('consumedStudyIds', nextIds);
    };

    const handleAdd = (study: StudyListSelectDto): void => {
      setIds([...studyIds, study.id]);
    };

    const handleRemove = (studyId: string): void => {
      setIds(studyIds.filter((id) => id !== studyId));
    };

    const picker = (
      <EntityPickerPopover<StudyListSelectDto>
        addButtonLabel="Add Study"
        emptyMessage="No available studies match this search."
        isLoading={isLoading}
        onAdd={handleAdd}
        onSearchChange={setSearch}
        options={filtered}
        placeholder="Search studies"
        renderOption={(study) => (
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{study.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {study.classificationLevel.toLowerCase()} · {study.status.toLowerCase()}
            </p>
          </div>
        )}
        search={search}
      />
    );

    return (
      <div className="flex flex-col gap-4">
        {attachedStudies.length === 0 ? (
          <EmptyStateContainer
            action={picker}
            className="min-h-[240px]"
            title="No Consumed Studies"
          />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <FormLabel label="Consumed Studies" />
            </div>
            <div className="flex flex-col gap-2">
              {attachedStudies.map((study) => (
                <div
                  className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2"
                  key={study.id}
                >
                  <div className="flex flex-col">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{study.name}</span>
                      <ClassificationBadge classification={study.classificationLevel} />
                      <StudyStatusBadge status={study.status} />
                    </div>
                  </div>
                  <IconButton
                    ariaLabel="Remove study"
                    icon={<Trash2Icon />}
                    onClick={() => handleRemove(study.id)}
                    size="md"
                    variant="secondary"
                  />
                </div>
              ))}
            </div>
            <div className="self-start">{picker}</div>
          </>
        )}
      </div>
    );
  },
});
